import { useState } from 'react';
import { Phone, MapPin, Mail, MessageCircle, Loader2, ArrowUpRight, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Reveal from '@/components/Reveal';
import Img from '@/components/Img';
import Seo from '@/components/Seo';
import Faq from '@/components/Faq';
import { CONTACT_PAGE } from '@/constants/testIds';
import { CONTACT, buildWhatsappLink, WHATSAPP } from '@/constants/site';
import { CONTACT_FAQS } from '@/constants/faqs';
import { submitContact } from '@/lib/api';

export default function Contact() {
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please share your name, email and message.');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error('Please enter a valid email.');
      return;
    }
    setLoading(true);
    try {
      const res = await submitContact(form);
      toast.success('Message sent. We will reply within one business day.');
      setDone(true);
      if (res?.whatsapp_link) {
        window.open(res.whatsapp_link, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Something went wrong. Please try WhatsApp or call.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={CONTACT_PAGE.root} className="bg-[hsl(var(--bb-ivory))]">
      <Seo
        title="Contact — Bombay Bespoke Fashion House | Al Karama, Dubai"
        description="Visit Bombay Bespoke Fashion House at Arif and Bintoak Building, Shop 8, Al Karama, Dubai. WhatsApp +971 54 279 1122, email info@bombaybespokefashion.com. Mon–Sat 10:00–21:00."
        canonical="https://bombaybespokefashion.com/contact"
      />
      <section className="bb-container pt-14 md:pt-24 pb-10">
        <Reveal>
          <div className="bb-eyebrow">Contact the House</div>
        </Reveal>
        <Reveal delay={120}>
          <h1 className="bb-display text-5xl md:text-7xl lg:text-8xl mt-4 text-[hsl(var(--bb-text))]">
            Write, call, or
            <br />
            <span className="bb-italic text-[hsl(var(--bb-gold-deep))]">walk in.</span>
          </h1>
        </Reveal>
      </section>

      <section className="bb-container pb-20 md:pb-28">
        <div className="grid grid-cols-12 gap-10 md:gap-16">
          {/* LEFT — details + buttons */}
          <div className="col-span-12 lg:col-span-5">
            <Reveal className="border border-[hsl(var(--bb-border))] p-7 md:p-9 bg-[hsl(var(--bb-ivory))]">
              <div className="space-y-7">
                <Detail
                  icon={<MapPin size={16} />}
                  label="Atelier"
                  body={<>{CONTACT.addressLine1}<br />{CONTACT.addressLine2}</>}
                />
                <Detail
                  icon={<Phone size={16} />}
                  label="Phone"
                  body={<a href={`tel:${CONTACT.phoneRaw}`} className="hover:text-[hsl(var(--bb-gold-deep))]">{CONTACT.phone}</a>}
                />
                <Detail
                  icon={<MessageCircle size={16} />}
                  label="WhatsApp"
                  body={<a href={buildWhatsappLink(WHATSAPP.defaultMessage)} target="_blank" rel="noreferrer" onClick={() => window.gtag && window.gtag('event', 'Whatsapp', { source: 'contact-detail' })} className="hover:text-[hsl(var(--bb-gold-deep))]">{CONTACT.phone}</a>}
                />
                <Detail
                  icon={<Mail size={16} />}
                  label="Email"
                  body={<a href={`mailto:${CONTACT.email}`} className="hover:text-[hsl(var(--bb-gold-deep))] break-all">{CONTACT.email}</a>}
                />
                <div>
                  <div className="bb-eyebrow">Hours</div>
                  <div className="mt-2 text-sm text-[hsl(var(--bb-text-muted))]">
                    {CONTACT.hours}<br />{CONTACT.hoursSunday}
                  </div>
                </div>
              </div>

              <div className="mt-9 grid grid-cols-1 gap-3">
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  data-testid={CONTACT_PAGE.callBtn}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.22em] text-[11px] font-medium hover:bg-[hsl(var(--bb-gold-deep))] transition-colors"
                >
                  <Phone size={15} /> Call now
                </a>
                <a
                  href={buildWhatsappLink(WHATSAPP.defaultMessage)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={CONTACT_PAGE.whatsappBtn}
                  onClick={() => window.gtag && window.gtag('event', 'Whatsapp', { source: 'contact-page' })}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3.5 border border-[hsl(var(--bb-emerald))] text-[hsl(var(--bb-emerald))] uppercase tracking-[0.22em] text-[11px] font-medium hover:bg-[hsl(var(--bb-emerald))] hover:text-[hsl(var(--bb-text))] transition-colors"
                >
                  <MessageCircle size={15} /> WhatsApp
                </a>
                <a
                  href={CONTACT.mapsLink}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={CONTACT_PAGE.directionsBtn}
                  className="inline-flex items-center justify-center gap-2 px-5 py-3.5 border border-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-text))] uppercase tracking-[0.22em] text-[11px] font-medium hover:bg-[hsl(var(--bb-gold))] hover:text-[hsl(var(--bb-charcoal))] transition-colors"
                >
                  <MapPin size={15} /> Get directions
                </a>
              </div>
            </Reveal>
          </div>

          {/* RIGHT — form */}
          <div className="col-span-12 lg:col-span-7">
            {done ? (
              <div
                data-testid={CONTACT_PAGE.success}
                className="border border-[hsl(var(--bb-gold))]/40 p-10 md:p-14 bg-[hsl(var(--bb-ivory-soft))]"
              >
                <div className="w-14 h-14 border border-[hsl(var(--bb-gold))]/50 flex items-center justify-center">
                  <Check size={26} className="text-[hsl(var(--bb-gold-deep))]" />
                </div>
                <h2 className="bb-display text-4xl md:text-5xl mt-6 text-[hsl(var(--bb-text))]">
                  Message <span className="bb-italic text-[hsl(var(--bb-gold-deep))]">received.</span>
                </h2>
                <p className="mt-5 text-[hsl(var(--bb-text-muted))] max-w-lg">
                  Thank you. We have your enquiry and will reply within one business day. A
                  WhatsApp draft has opened in a new tab — tap send to notify us instantly.
                </p>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid={CONTACT_PAGE.form}
                className="border border-[hsl(var(--bb-border))] p-8 md:p-12 grid grid-cols-12 gap-x-8 gap-y-7"
              >
                <Field label="Full Name" cols="col-span-12 md:col-span-6">
                  <Input
                    data-testid={CONTACT_PAGE.inputName}
                    value={form.full_name}
                    onChange={(e) => update('full_name', e.target.value)}
                    className="bb-input"
                    placeholder="Your name"
                  />
                </Field>
                <Field label="Email" cols="col-span-12 md:col-span-6">
                  <Input
                    data-testid={CONTACT_PAGE.inputEmail}
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    className="bb-input"
                    placeholder="you@email.com"
                  />
                </Field>
                <Field label="Phone (optional)" cols="col-span-12 md:col-span-6">
                  <Input
                    data-testid={CONTACT_PAGE.inputPhone}
                    value={form.phone}
                    onChange={(e) => update('phone', e.target.value)}
                    className="bb-input"
                    placeholder="+971 …"
                  />
                </Field>
                <Field label="Subject (optional)" cols="col-span-12 md:col-span-6">
                  <Input
                    data-testid={CONTACT_PAGE.inputSubject}
                    value={form.subject}
                    onChange={(e) => update('subject', e.target.value)}
                    className="bb-input"
                    placeholder="Bridal lehenga · Suit · Other"
                  />
                </Field>
                <Field label="Message" cols="col-span-12">
                  <Textarea
                    data-testid={CONTACT_PAGE.inputMessage}
                    rows={5}
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    className="bb-input bb-textarea"
                    placeholder="Tell us a little about your enquiry…"
                  />
                </Field>
                <div className="col-span-12 flex flex-wrap items-center justify-between gap-4 pt-2">
                  <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--bb-text-muted))]">
                    We respond within one business day.
                  </p>
                  <button
                    type="submit"
                    data-testid={CONTACT_PAGE.submit}
                    disabled={loading}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.24em] text-[11px] font-medium hover:bg-[hsl(var(--bb-gold-deep))] transition-colors disabled:opacity-60"
                  >
                    {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send message <ArrowUpRight size={16} /></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* MAP */}
      <section className="relative">
        <div className="w-full aspect-[16/9] md:aspect-[21/7] grid grid-cols-12">
          <div className="hidden md:block md:col-span-5 relative overflow-hidden">
            <Img src="/assets/image-1.jpg" alt="Bombay Bespoke storefront" imgClassName="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[hsl(var(--bb-text))]/0" />
            <div className="absolute left-6 bottom-6 right-6">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--bb-gold))]">The Storefront</div>
              <div className="mt-2 bb-display text-2xl md:text-3xl text-[hsl(var(--bb-text))]">
                Walk in. <span className="bb-italic text-[hsl(var(--bb-gold))]">We’ll have the kettle on.</span>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-7">
            <iframe
              title="Bombay Bespoke Fashion House Dubai"
              src={CONTACT.mapsEmbed}
              className="w-full h-full grayscale-[40%] contrast-[1.05]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <section className="bg-[hsl(var(--bb-ivory-soft))] border-t border-[hsl(var(--bb-text))]/8">
        <Faq
          eyebrow="Contact · FAQs"
          heading="Finding us,"
          italicWord="made simple."
          items={CONTACT_FAQS}
          tone="gold"
        />
      </section>

      <style>{`
        .bb-input {
          width: 100%;
          background: transparent;
          border: none;
          border-bottom: 1px solid hsl(var(--bb-text) / 0.22);
          border-radius: 0;
          padding: 14px 0 12px;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          color: hsl(var(--bb-text));
          outline: none;
          transition: border-color 0.4s ease;
        }
        .bb-input::placeholder { color: hsl(var(--bb-text-muted)); opacity: 0.7; }
        .bb-input:focus { border-bottom-color: hsl(var(--bb-gold)); }
        .bb-textarea { resize: vertical; min-height: 110px; }
      `}</style>
    </div>
  );
}

function Field({ label, cols, children }) {
  return (
    <div className={cols}>
      <Label className="block text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--bb-text-muted))] mb-2">{label}</Label>
      {children}
    </div>
  );
}

function Detail({ icon, label, body }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-1 text-[hsl(var(--bb-gold-deep))]">{icon}</span>
      <div>
        <div className="bb-eyebrow">{label}</div>
        <div className="mt-1 text-[hsl(var(--bb-text))] text-sm md:text-base">{body}</div>
      </div>
    </div>
  );
}